import { useCallback, useEffect, useMemo, useState } from "react";
import EmptyState from "@/components/base/EmptyState";
import PreuvePaiementModal from "@/pages/super-admin/components/PreuvePaiementModal";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { cn } from "@/utils/cn";
import { formatFcfa } from "@/utils/format";
import { fetchMesPaiements } from "./data";
import type { MonPaiementAbonnement } from "./types";
import PaiementMobileCard from "./components/PaiementMobileCard";
import PaiementTable from "./components/PaiementTable";

type FiltreStatut = "tous" | "en_attente" | "valide" | "rejete";

const FILTRES: { value: FiltreStatut; label: string }[] = [
  { value: "tous", label: "Tous" },
  { value: "en_attente", label: "En attente" },
  { value: "valide", label: "Validés" },
  { value: "rejete", label: "Refusés" },
];

/**
 * Module client « Mes paiements » : historique réel des paiements d'abonnement
 * du compte connecté. Lecture seule — l'isolation par compte est assurée par le
 * RLS Supabase (aucun identifiant de compte transmis par le navigateur).
 */
export default function MesPaiementsPage() {
  const [paiements, setPaiements] = useState<MonPaiementAbonnement[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<FiltreStatut>("tous");
  const [preuve, setPreuve] = useState<MonPaiementAbonnement | null>(null);

  const charger = useCallback(async () => {
    try {
      setChargement(true);
      setErreur(null);
      const data = await fetchMesPaiements();
      setPaiements(data);
    } catch {
      setErreur("Impossible de charger vos paiements pour le moment. Vérifiez votre connexion puis réessayez.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const stats = useMemo(
    () => ({
      total: paiements.length,
      enAttente: paiements.filter((item) => item.statut === "en_attente").length,
      valides: paiements.filter((item) => item.statut === "valide").length,
      montantPaye: paiements
        .filter((item) => item.statut === "valide")
        .reduce((total, item) => total + item.montant, 0),
    }),
    [paiements]
  );

  const filtres = useMemo(
    () => (filtre === "tous" ? paiements : paiements.filter((item) => item.statut === filtre)),
    [paiements, filtre]
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-2xl">
        <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Mes paiements</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
          Consultez l'historique réel de vos paiements d'abonnement : montant, période, moyen de paiement, référence
          et statut. Les paiements sont vérifiés et validés par notre équipe.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-receipt-line" label="Paiements" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-hourglass-line" label="En attente" value={String(stats.enAttente)} tone="accent" />
        <StatCard icon="ri-checkbox-circle-line" label="Validés" value={String(stats.valides)} tone="secondary" />
        <StatCard icon="ri-money-cny-circle-line" label="Montant validé" value={formatFcfa(stats.montantPaye)} tone="neutral" />
      </div>

      {!chargement && !erreur && paiements.length > 0 && (
        <div className="flex max-w-full items-center gap-2">
          <div className="inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-background-200 bg-background-100 p-1">
            {FILTRES.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFiltre(option.value)}
                className={cn(
                  "shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label font-medium transition-all duration-200 cursor-pointer",
                  filtre === option.value
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {chargement && (
        <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-16">
          <div className="flex flex-col items-center gap-3">
            <span className="h-7 w-7 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
            <p className="text-sm text-foreground-500">Chargement de vos paiements…</p>
          </div>
        </div>
      )}

      {!chargement && erreur && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800" role="alert">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {erreur}
          </p>
          <button
            type="button"
            onClick={() => void charger()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!chargement && !erreur && paiements.length === 0 && (
        <EmptyState
          icon="ri-receipt-line"
          title="Aucun paiement d'abonnement"
          description="Vous n'avez encore aucun paiement d'abonnement. Vos futures demandes et leur statut apparaîtront ici."
        />
      )}

      {!chargement && !erreur && paiements.length > 0 && filtres.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun paiement pour ce filtre"
          description="Aucun paiement ne correspond au statut sélectionné. Choisissez un autre filtre pour voir vos paiements."
        />
      )}

      {!chargement && !erreur && filtres.length > 0 && (
        <>
          <div className="hidden md:block">
            <PaiementTable paiements={filtres} onViewPreuve={setPreuve} />
          </div>
          <div className="flex flex-col gap-4 md:hidden">
            {filtres.map((paiement) => (
              <PaiementMobileCard key={paiement.id} paiement={paiement} onViewPreuve={setPreuve} />
            ))}
          </div>
        </>
      )}

      <PreuvePaiementModal
        open={Boolean(preuve)}
        onClose={() => setPreuve(null)}
        path={preuve?.preuveUrl ?? ""}
        reference={preuve?.reference ?? ""}
      />
    </div>
  );
}